'use client';

// Alpha Zone Next 10 — "on deck" names approaching the basket.
//
// The held basket is fetched from /api/alpha-basket and subtracted before
// ranking. This prevents a held name (e.g. MA at raw rank #15) from
// appearing here as "on deck" even though it is already held.
//
// Renders via AlphaZoneHoldingsTable so the layout is byte-for-byte
// identical to the held-10 table (same columns, same styling, same
// signal cells). Held-only fields (enteredOn / daysIn / pctSinceAddition /
// pctReturn / weight) are null for on-deck names and render as "—".
//
// onlyNext prop is kept for backward compat with app/alpha-zone/page.tsx
// which passes <AlphaZoneNext10 onlyNext />.  The combined 1-20 mode is
// simplified away — the page already shows the held-10 via AlphaZoneTracker
// slot="holdings" in the sibling tab.
import { useMemo } from 'react';
import { useResource } from '@/lib/use-resource';
import { compositeScore, isAlphaZoneEligible } from '@/lib/composite-score';
import { AlphaZoneHoldingsTable, type AlphaPerStockRow } from './AlphaZoneHoldingsTable';
import type { ScoredStock } from '@/lib/types';

interface StocksResponse { stocks: ScoredStock[]; updatedAt?: string }

// /api/alpha-basket envelope: { basket: { slots: [...] }, displaySlots, ... }
interface BasketResp {
  basket?: { slots?: Array<{ current: { symbol: string } }> };
  displaySlots?: Array<{ current: { symbol: string } }>;
}

// Production V: blended when available, else peer-z — matches the basket
// eligibility / composite read so the ranking can't drift from selection.
const vOf = (s: ScoredStock): number | null =>
  (s.valuationScoreBlended ?? s.valuationScore) ?? null;

// Today as ISO YYYY-MM-DD — used as the anchorDate for AlphaZoneHoldingsTable.
// On-deck names have no basket-entry date, so the anchor is informational only.
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// onlyNext is kept to avoid changing app/alpha-zone/page.tsx.
// When false (rarely used externally) we still render only the on-deck names.
export function AlphaZoneNext10({ onlyNext = false }: { onlyNext?: boolean } = {}) {
  // Suppress the unused-variable warning for the backward-compat prop.
  void onlyNext;

  const { data: stocksData, loading: stocksLoading } = useResource<StocksResponse>('/api/stocks');
  const { data: basketData, loading: basketLoading } = useResource<BasketResp>('/api/alpha-basket');

  const onDeckRows: AlphaPerStockRow[] = useMemo(() => {
    const stocks = stocksData?.stocks ?? [];
    if (stocks.length === 0) return [];

    // Canonical held-set pattern from AlphaZoneAttribution / AlphaZoneVisualizer:
    // prefer displaySlots (BUY+ filtered) → fall back to full basket slots.
    const slots = basketData?.displaySlots ?? basketData?.basket?.slots ?? [];
    const heldSet = new Set(slots.map((s) => s.current.symbol));

    // Rank all eligible stocks by composite (blended Q + blended V).
    const ranked = stocks
      .map((s) => ({
        s,
        v: vOf(s),
        c: compositeScore(
          (s.qualityScoreBlended ?? s.qualityScore) ?? null,
          vOf(s),
        ),
      }))
      .filter(
        (r) =>
          isAlphaZoneEligible(
            (r.s.qualityScoreBlended ?? r.s.qualityScore) ?? null,
            r.v,
          ) &&
          r.c != null &&
          Number.isFinite(r.c),
      )
      .sort((a, b) => (b.c as number) - (a.c as number));

    // On-deck = top names NOT already held in the basket, take 10.
    const onDeck = ranked.filter((r) => !heldSet.has(r.s.symbol)).slice(0, 10);

    // Map to AlphaPerStockRow. Held-only fields are null (render as "—").
    return onDeck.map((r): AlphaPerStockRow => ({
      symbol: r.s.symbol,
      name: r.s.name ?? null,
      sector: r.s.sector ?? null,
      enteredOn: null,
      daysIn: null,
      pctSinceAddition: null,
      pctReturn: null,
      weight: null,
    }));
  }, [stocksData, basketData]);

  if (stocksLoading || basketLoading) {
    return (
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-6 text-center text-[#64748b] text-sm">
        loading on-deck names…
      </div>
    );
  }
  if (onDeckRows.length === 0) {
    return (
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-6 text-center text-[#64748b] text-sm">
        no eligible on-deck names
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-2">
        <h3 className="text-[15px] font-bold text-white">Next 10 — On Deck</h3>
        <span className="text-[12px] text-[#64748b]">
          approaching the Alpha Zone · ranked by composite · excludes held names
        </span>
      </div>
      <AlphaZoneHoldingsTable
        perStock={onDeckRows}
        anchorDate={todayIso()}
      />
    </div>
  );
}
